import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Dropdown } from 'primereact/dropdown'
import { InputTextarea } from 'primereact/inputtextarea'
import { Calendar } from 'primereact/calendar'
import { Dialog } from 'primereact/dialog'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import Button from '../../components/ui/Button'
import { useToast } from '../../components/ui/ToastProvider'
import { useCurrentUser, useGrants } from '../../auth/useCurrentUser'
import { GRANTS, type GrantCode } from '../../auth/grants'
import { playSuccessSound } from '../../lib/sound'
import {
  deadlineMinDate,
  formatDate,
  parseISODate,
  todayStart,
  toISODate,
} from '../../lib/datetime'
import {
  useActionDetail,
  useAllowedTransitions,
  useChangeActionStatus,
  useDeleteActionNote,
  useSetSubActionAssignee,
  useUpdateActionNote,
  useUpdateSubAction,
  useUploadAttachments,
} from './hooks/useActionDetail'
import ActionHistoryDialog from './components/ActionHistoryDialog'
import ActionStatusTag from './components/ActionStatusTag'
import ActionCompletionNote from './components/ActionCompletionNote'
import CompletionNoteDialog from './components/CompletionNoteDialog'
import ActionDocumentsSection from './components/ActionDocumentsSection'
import ActionTerminCard from './components/ActionTerminCard'
import { ActionNotesSection, NoteEditDialog } from './components/ActionNotesSection'
import DeadlineRequestDialog from './components/DeadlineRequestDialog'
import { useCreateDeadlineRequest } from './hooks/useCreateDeadlineRequest'
import MainTaskViewDialog from './components/MainTaskViewDialog'
import SinglePersonEditor from './components/SinglePersonEditor'
import {
  documentMutationErrorKey,
  noteMutationErrorKey,
  statusChangeErrorKey,
  subActionSaveErrorKey,
} from './statusError'
import { useActionStatusName } from './hooks/useActionStatuses'
import { useProxyMode } from '../proxy/useProxyMode'
import {
  ACTION_STATUS,
  baseStatusOf,
  minutePersonToInput,
  personInputToMinutePerson,
  type ActionDetail,
  type ActionNote,
  type MinutePerson,
} from './types'
import '../minutes/minutes.css'
import '../meetings/meetings.css'
import './actions.css'

/**
 * Alt Görev Detay — <b>ayrı sayfa</b> (066). Tablo alanları (No · Tanım · Atanan · Hedef Teslim Tarihi ·
 * Durum · Görevi Oluşturan) <b>+ Görev Notu + Dokümanlar</b>; koordine/takip/iç-alt-görev YOKtur
 * (alt görev sadeliği).
 *
 * <p><b>Not alanı 2026-08-28'de eklendi</b> (kullanıcı isteği: "tıpkı ana görev gibi") — 066/AK-7'nin
 * "alt görevde not yoktur" kuralı kalktı. Yüzey ana görevle <b>aynı bileşendir</b>
 * ({@code components/ActionNotesSection}) ve kural da aynıdır: hedef teslim tarihi değişince not
 * zorunlu, {@code ACTION_NOTE_MANAGE} ile düzenlenip silinebilir. Alan düzenleme yönetim
 * ({@code MINUTES_CREATE}); statü değişimi sahip (atanan) veya yönetim. Termini geçmiş alt görevi açık bir
 * statüye çekerken yeni hedef teslim tarihi zorunlu (AK-12); <b>o akışta</b> not hâlâ istenmez — statü
 * değişimi ayrı bir uçtur ({@code ActionCommandService}) ve kuralı ayrıca kararlaştırılmalıdır. Kök görev
 * id'si gelirse normal Görev Detay'a yönlendirir.
 *
 * <p><b>Doküman bölümü 2026-09-10'da eklendi</b> (kullanıcı isteği: "ana görevdeki gibi") —
 * AK-7'nin <i>"alt görevde doküman yoktur"</i> kuralı, not kolundan sonra bu kolla da kalktı.
 * Bileşen ve kural ana görevle aynıdır ({@code ActionDocumentsSection}; ekle/kaldır
 * {@code ACTION_MANAGE} + sonlanmamış statü, indirme herkese açık).
 *
 * <p><b>Görevi Oluşturan</b> hücresi de aynı gün eklendi — sunucu {@code createdByName}'i zaten her
 * görev için çözüyordu, alt görev ekranı yalnız göstermiyordu.
 */
/**
 * <b>Kaydın kendisinden</b> türeyen olgular — yetki sormaz, yalnız veriye bakar.
 *
 * <p>Bileşenden ayrıldı (2026-09-15): {@code SubActionDetailPage} bilişsel karmaşıklık eşiğini aşmıştı
 * (SonarQube S3776, 17/15). Ayırım keyfi değil: burada "kayıt ne durumda", bileşende "bu kullanıcı ne
 * yapabilir" sorusu var — ikincisi yetki (grant) okumadan cevaplanamaz, birincisi ise saf.
 *
 * <p><b>{@code ?? false} / {@code ?? null} yedekleri fail-closed'dır:</b> alan yanıtta gelmezse kayıt
 * düzenlenemez sayılır. Sunucunun salt-okuma kararı (2026-08-26) istemcide yeniden hesaplanmaz.
 *
 * <p><b>{@code isOverdue} "bugün"ü KURUMSAL günden alır</b> ({@code todayStart} → Europe/Istanbul),
 * tarayıcının yerel gününden değil: sunucu ({@code ActionCommandService.authorizeSubAction}) o güne
 * bakıyor. Ayrışırsa saat dilimi farklı bir istemcide Gecikti-çıkış popup'ı hiç açılmaz ve kullanıcı
 * düz statü değişiminde 400 {@code deadlineRequired} alır.
 */
function subActionFacts(data: ActionDetail | undefined, meEmployeeId: string | undefined) {
  const statusId = data?.statusId ?? null
  const deadline = parseISODate(data?.deadline ?? '')
  return {
    serverEditable: data?.editable ?? false,
    infoEditable: data?.infoEditable ?? false,
    /** Hedef teslim tarihi — sunucudan, fail-closed (130: `ACTION_DEADLINE_UPDATE` ∨ oluşturan). */
    deadlineEditable: data?.deadlineEditable ?? false,
    statusId,
    /**
     * Sonlanmış (salt-okuma) statüler: **Kapatıldı / Arşivlendi**.
     *
     * <p><b>Tamamlandı 22.09.2026'da BURADAN ÇIKTI</b> (kullanıcı bildirimi): alt görev artık
     * Tamamlandı'dan Aksiyon Bekleniyor'a ya da Kapatıldı'ya çekilebiliyor, yani o statü son durum
     * değil. Liste burada kaldığı sürece ekran statü seçiciyi hiç çizmiyordu — yetkisi olan kullanıcı
     * (oluşturan, Özel Kalem, Süper Admin) sunucu izin verdiği hâlde durumu değiştiremiyordu.
     */
    terminal: statusId === ACTION_STATUS.CLOSED || statusId === ACTION_STATUS.ARCHIVED,
    /**
     * İÇERİK kilidi (ad/açıklama/tarih, "Ata", not, doküman) — <b>Tamamlandı dâhil</b>.
     *
     * <p>{@code terminal} 22.09.2026'da daraldı (331 çıktı) ama o değişikliğin amacı yalnız statü
     * seçicisiydi. İçerik kolları o ölçütü paylaşsaydı, Tamamlandı bir alt görevde ad/tarih düzenleme,
     * "Ata", not ve dosya <b>sessizce açılırdı</b> — bu commit'in istenmeyen yan etkisi olurdu ve
     * 2026-08-28'den beri bilinçle korunan bir kural kalkmış olurdu (bağımsız inceleme bulgusu).
     */
    contentLocked:
      statusId === ACTION_STATUS.COMPLETED ||
      statusId === ACTION_STATUS.CLOSED ||
      statusId === ACTION_STATUS.ARCHIVED,
    isOwner: !!meEmployeeId && (data?.assignees ?? []).some((a) => a.employeeId === meEmployeeId),
    isOverdue: !!deadline && deadline < todayStart(),
  }
}

/**
 * <b>Bu kullanıcı ne yapabilir</b> — kaydın olguları ({@link subActionFacts}) + yetkiler.
 *
 * <p>Bileşenden ayrıldı (2026-09-15): {@code SubActionDetailPage} bilişsel karmaşıklık eşiğini aşmıştı
 * (SonarQube S3776, 17/15) ve karmaşıklık tek bir blokta değil, bu bayrakların her birine dağılmıştı.
 * Kuralların kendisi DEĞİŞMEDİ — yalnız bir arada, gerekçeleriyle okunabilecekleri yere taşındılar.
 */
function subActionPermissions(
  facts: ReturnType<typeof subActionFacts>,
  hasGrant: (g: GrantCode) => boolean,
  /** Kısıtlı Yetkili asistan kipi — her yazma yüzeyini kapatır (126 FR-019). */
  readOnly: boolean,
) {
  const { serverEditable, infoEditable, deadlineEditable, terminal, contentLocked, isOwner } = facts
  /**
   * Alt görevde **alan düzenleme (ad / açıklama)** — **yönetim** ({@code MINUTES_CREATE})
   * ya da alt görevi **oluşturan** (kullanıcı kuralları 2026-08-30 ve 2026-08-31).
   *
   * <p>2026-08-30: ölçüt {@code ACTION_MANAGE} idi ve o yetki **Yönetici/GMY rollerinde de var**;
   * sonuçta alt görevin SORUMLUSU olan bir Yönetici kaydın adını ve hedef teslim tarihini
   * değiştirebiliyordu.
   * Kural: sorumlu o kayıtta yalnız **durum + hedef teslim tarihi talebi + not** yapabilir.
   *
   * <p>2026-08-31: *"alt görevi oluşturan kendi ise onda da değişiklik yapabilir"* → muafiyete
   * **oluşturan** eklendi. Bu koşul istemcide **ölçülemez** (yanıtta yalnız oluşturanın görünen adı
   * var, ada göre kıyas kırılgan) → izin sunucudan {@code ActionDetail.infoEditable} ile gelir. Alan
   * gelmezse **fail-closed**; `serverEditable` ile aynı desen.
   *
   * <p>Sunucu bu alanı **kayıt türüne göre** hesaplar: alt görevde yalnız *yönetim veya oluşturan*,
   * kök görevde ayrıca *Ana Sorumlu değil* kolu vardır. Fark bilinçlidir — alt görevin atananı DB'de
   * o kaydın Ana Sorumlusudur, o kol burada olsaydı ÜST görevin Ana Sorumlusu alt görevin adını da
   * düzenlerdi ({@code ActionDetailService#isTaskInfoEditable}).
   *
   * <p><b>"Ata" bu ölçütün DIŞINDADIR</b> (kullanıcı kuralı 2026-08-31) — bkz. {@code canAssign}.
   */
  const canManage = infoEditable && serverEditable
  /**
   * Statü seçicisinin **görünürlüğü**: kayıt sonlanmamış ve sunucu yazma izni vermiş olmalı.
   *
   * <p><b>Kimin hangi hedefi seçebileceğini istemci ARTIK TÜRETMEZ</b> (22.09.2026): hedef listesi
   * {@code allowedTargets} ucundan gelir ve boşsa seçici zaten çizilmez. Önceki ölçüt
   * ({@code canManageStatus || isOwner}) yeni kuralı karşılamıyordu — alt görevi Tamamlandı'dan geri
   * çekme hakkı görevi **oluşturana** da açıldı, oysa "oluşturan mıyım" bilgisi istemcide yok
   * (detay yanıtı yalnız oluşturanın ADINI taşır). İstemci kopyası kalsaydı oluşturan, sunucunun
   * izin verdiği geçişi ekranda göremezdi.
   */
  // Kısıtlı Yetkili asistan kipinde statü değiştirilemez (126 FR-019). Kök görevde de AÇIKTI ve aynı
  // turda kapandı: oradaki koşul `canManageStatus || isMainResponsible` idi, yani yetki kolu varken
  // ilişki kolu onu atlıyordu. Alt görevde ölçüt
  // bilinçle sunucuya bırakıldığı için (22.09.2026) hiçbir istemci kapısı yoktu ve bu kip de
  // sorulmuyordu — push kapısı review'ü yakaladı.
  const canChangeStatus = !terminal && serverEditable && !readOnly
  // `!readOnly` burada da gerekli: `canManage` SUNUCUDAN gelir ve hiçbir yetki kodu sormaz
  // (ölçüt `MINUTES_CREATE || isCreator` ve `isCreator` persona kipinde HEDEFİ okur). Bugün yazma
  // yolu yok — tek "Kaydet" `ACTION_MANAGE` ile kapalı — ama alanlar düzenlenebilir GÖRÜNÜYORDU,
  // zorunluluk yıldızlarıyla birlikte (push kapısı review'ü).
  const fieldsEditable = canManage && !contentLocked && !readOnly
  /**
   * "Ata" (sorumluyu değiştir) — alan düzenlemeden AYRI ölçüt (kullanıcı kuralı 2026-08-31:
   * *"yönetici rolündeysem ve sorumluysam alt görevin atama işlemini de yapabilmeliyim"*).
   *
   * <p>Önceden buton {@code fieldsEditable}'a bağlıydı, yani {@code MINUTES_CREATE} istiyordu; oysa
   * Yönetici (Executive) rolünde o yetki YOK, {@code SUBACTION_ASSIGNEE_UPDATE} ise VAR (075 seed).
   * Sonuç: sunucu izin verdiği hâlde ekran butonu gizliyordu — uç yalnız
   * {@code Grants#HAS_SUBACTION_ASSIGNEE_UPDATE} + yazma kapsamı arar
   * ({@code ActionController#setSubActionAssignee}), {@code MINUTES_CREATE} istemez. Yeni ölçüt bu
   * yüzden sunucunun kuralının aynasıdır; ek olarak **sorumlu olma** koşulu aranır (kullanıcı
   * kararı): yetkisi olup kaydın sorumlusu OLMAYAN bir kullanıcı için buton yalnız `canManage`
   * kolundan gelebilir. **2026-08-31'de o kol genişledi:** alt görevi **oluşturan** (atananı olmasa
   * da) `canManage` alır, dolayısıyla "Ata"yı görür. Uç bundan zaten geniştir (yalnız
   * `SUBACTION_ASSIGNEE_UPDATE` + yazma kapsamı arar), yani yön güvenli: ekran uçtan dar.
   *
   * <p>Yetkinin kendisi ({@code SUBACTION_ASSIGNEE_UPDATE}) hâlâ zorunludur — kaldırılan tek şey
   * {@code MINUTES_CREATE} bağımlılığıdır; {@code serverEditable} ve terminal statü kapıları aynen
   * durur.
   */
  const canAssign =
    hasGrant(GRANTS.SUBACTION_ASSIGNEE_UPDATE) &&
    serverEditable &&
    !contentLocked &&
    (canManage || isOwner)
  // Not düzenle/sil: yetki + sonlanmamış statü (ana görevdeki `notesManageable` ile aynı ölçüt).
  const notesManageable = hasGrant(GRANTS.ACTION_NOTE_MANAGE) && serverEditable && !contentLocked
  /**
   * Not **ekleme** — alan düzenlemeden AYRI (kullanıcı kuralı 2026-08-30): alt görevin sorumlusu, ad ve
   * hedef teslim tarihini değiştiremese de not ekleyebilir. Bu yüzden ölçüt `fieldsEditable` değil,
   * "yönetim VEYA sorumlu". Sunucu da aynı ayrımı yapar: aynı uca gider ama yalnız `note` alanının
   * değişmesine izin verir.
   */
  /**
   * `ACTION_MANAGE` grant'i de aranır (bağımsız code-review bulgusu 2026-09-10): not ucu
   * (`PUT /{id}/subaction`) ve doküman uçları (`POST|DELETE /{id}/attachments`) **ikisi de**
   * `@PreAuthorize(HAS_ACTION_MANAGE)` ile korunur; ekran bunu sormazsa uçtan GENİŞ olur ve
   * yetkisiz kullanıcıya "Not Ekle"/"Dosya Ekle" çizilip 403 alınırdı.
   *
   * Bugün ısırmıyor — `SUBACTION_PAGE_VIEW` taşıyan dört rolün dördü de `ACTION_MANAGE` taşıyor
   * (075 seed) — ama `SUBACTION_PAGE_VIEW` ileride başka bir role verilirse ayrışma açığa çıkardı.
   */
  const notesAddable =
    hasGrant(GRANTS.ACTION_MANAGE) && (canManage || isOwner) && serverEditable && !contentLocked
  /**
   * Doküman ekle/kaldır (2026-09-10) — ölçüt **NOT EKLEME ile aynı** (kullanıcı kararı):
   * `notesAddable`. Yani `fieldsEditable` DEĞİL — alt görevin **sorumlusu** ad/hedef teslim
   * tarihini düzenleyemese de not ekleyebildiği gibi **dosya da ekleyebilir**.
   *
   * İki yüzeyin ölçütü bilinçle ORTAKtır ve tek bir değişkenden okunur: ayrı yazılsalardı birine
   * konan bir kapı diğerinde unutulur ve kullanıcı "not ekleyebiliyorum ama dosya ekleyemiyorum"
   * gibi açıklanamayan bir farkla karşılaşırdı. Sunucu tarafında da aynı yerdeler: her ikisi de
   * `ACTION_MANAGE` + kayıt-düzeyi yazma izni + **sonlanmamış** statü ister (not `PUT
   * /{id}/subaction`, doküman `POST|DELETE /{id}/attachments`).
   *
   * **Bir sapma var ve bilinçle KORUNUYOR:** buradaki ölçüt (`contentLocked`) **Tamamlandı(331)'i de** sayar,
   * oysa sunucunun `TERMINAL_STATUSES`'ü yalnız Kapatıldı+Arşivlendi'dir (ana görev ekranı da
   * öyle). Yani Tamamlandı bir alt görevde ekran ekleme/kaldırmayı kapatır, uç kabul ederdi.
   * Sapma 2026-08-28'den beri **not** kolunda vardı; doküman ona eşitlendiği için aynen devraldı.
   * Yön güvenli (ekran uçtan dar → kırık buton yok) ve iki kolu ayırmak "not ekleyemiyorum ama
   * dosya ekleyebiliyorum" farkı üretirdi; düzeltilecekse **ikisi birlikte** ele alınmalı.
   *
   * <p><b>22.09.2026:</b> statü seçicisinin ölçütü (`terminal`) Tamamlandı'yı bırakırken içerik
   * kolları AYRI bir bayrağa (`contentLocked`) taşındı — aksi halde bu sapma sessizce kalkardı.
   */
  const documentsEditable = notesAddable
  /**
   * **Hedef teslim tarihi** — ad/açıklamadan (`fieldsEditable`) AYRI ölçüt (130, 2026-11-02): sunucu
   * `deadlineEditable` = `ACTION_DEADLINE_UPDATE` ∨ oluşturan. `canManage` bilinçle ARANMAZ: o
   * `infoEditable`'ı, yani `MINUTES_CREATE` ∨ oluşturan'ı içerir ve yetkiyi Rol Yönetimi'nden alan
   * `MINUTES_CREATE`'siz bir rolü dışarıda bırakırdı (analyze A1). Ön şartlar uçla aynı:
   * `ACTION_MANAGE` (`PUT /{id}/subaction` kapısı), kayıt-düzeyi yazma izni, içerik kilidi yok.
   */
  const deadlineFieldEditable =
    hasGrant(GRANTS.ACTION_MANAGE) && deadlineEditable && serverEditable && !contentLocked && !readOnly
  return {
    canManage,
    canChangeStatus,
    fieldsEditable,
    deadlineFieldEditable,
    canAssign,
    notesManageable,
    notesAddable,
    documentsEditable,
  }
}

export default function SubActionDetailPage() {
  const { t } = useTranslation()
  const toast = useToast()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const actionId = id ? Number(id) : null
  const aid = actionId ?? 0
  const { data, isError } = useActionDetail(actionId)

  const { hasGrant, hasAnyGrant } = useGrants()
  const { readOnly } = useProxyMode()
  const { data: me } = useCurrentUser()
  /**
   * Sunucunun salt-okuma kararı (2026-08-26) — bkz. `ActionDetailPage`. Alt görevde de geçerlidir:
   * yazma kapsamı kaydı **üstü/altları/kardeşleri** üzerinden değerlendirir, dolayısıyla ÜST görevi
   * yalnız takip eden kullanıcı alt görevi de değiştiremez. Alt göreve **atanan** kişi etkilenmez.
   */
  const facts = subActionFacts(data, me?.employeeId)
  const { statusId, isOverdue } = facts
  const {
    canChangeStatus,
    fieldsEditable,
    deadlineFieldEditable,
    canAssign,
    notesManageable,
    notesAddable,
    documentsEditable,
  } = subActionPermissions(facts, hasGrant, readOnly)
  // Gecikti-çıkış popup'ında yeni termin "bugün veya sonrası" olmalı → takvim alt sınırı bugün (kurumsal).
  const overdueExitMinDate = todayStart()

  const update = useUpdateSubAction(aid)
  const updateNoteMut = useUpdateActionNote(aid)
  const deleteNoteMut = useDeleteActionNote(aid)
  const reassign = useSetSubActionAssignee(aid) // 073 — Sorumlu (Atanan) değiştir (SAP/HR)
  const changeStatus = useChangeActionStatus(aid)
  // Onaya gönderme pop-up'ının dosya alanı için; Dokümanlar bölümü kendi örneğini ayrıca kurar
  // (aynı uç, aynı tazeleme anahtarı — iki örneğin bekleyen durumu birbirine karışmaz).
  const uploadAttachments = useUploadAttachments(aid)
  const transitions = useAllowedTransitions(actionId, statusId)

  // Düzenlenebilir tablo alanları — detaydan başlatılır. (Atanan yerel state tutulmaz → reassign sonrası
  // her zaman `data`'dan taze gösterilir; aksi halde initedRef aynı id'de yeniden okumadığından bayat kalırdı.)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [deadline, setDeadline] = useState('')
  const [assignOpen, setAssignOpen] = useState(false) // 073 — "Ata" (Sorumlu değiştir) dialog'u
  // "Ana Görevi Gör" popup'ı (2026-09-10) — salt-okuma; veri yalnız açıkken çekilir.
  const [mainTaskOpen, setMainTaskOpen] = useState(false)
  /** 074 — Görev Geçmişi dialog'u; veri yalnız açıkken çekilir. */
  const [historyOpen, setHistoryOpen] = useState(false)
  const [selected, setSelected] = useState<MinutePerson | null>(null) // 073 — dialog'da seçilen (henüz gönderilmedi)
  // 2026-08-05 — Gecikti alt aksiyonu açık statüye çekerken yeni termin popup'ta alınır (alt aksiyonda NOT yok).
  const [exitTarget, setExitTarget] = useState<number | null>(null)
  /** Gecikti-çıkış gerekçesi (22.09.2026 — alt görevde de ana görevdeki gibi sorulur). */
  const [exitNote, setExitNote] = useState('')
  const [exitDeadline, setExitDeadline] = useState('')
  // 105 — Tamamlandı(331) hedefinde araya giren onaya gönderme notu pop-up'ı (ana görevle aynı akış).
  const [completionNoteOpen, setCompletionNoteOpen] = useState(false)
  const [deadlineRequestOpen, setDeadlineRequestOpen] = useState(false) // 087 — termin talebi pop-up'ı
  // Görev Notu (2026-08-28; ana görevle aynı yüzey): girilen metin + "Not Ekle" pop-up'ı, ve
  // ACTION_NOTE_MANAGE ile düzenlenen notun taslağı (null → düzenleme pop-up'ı kapalı).
  const [note, setNote] = useState('')
  const [noteDialogOpen, setNoteDialogOpen] = useState(false)
  const [editNote, setEditNote] = useState<ActionNote | null>(null)
  const [editNoteText, setEditNoteText] = useState('')

  // 087 — Buton görünürlüğü SUNUCUDAN gelir (data.deadlineRequestAllowed); uygunluk kuralı burada
  // yeniden hesaplanmaz (Aksiyon Detay ile aynı desen). İstemcideki tek kontrol, butona basıldığında
  // takvimde seçilebilir gün kalıp kalmadığıdır (`noRequestableDate`, aşağıda).
  const createDeadlineRequest = useCreateDeadlineRequest(actionId)
  const submitDeadlineRequest = (input: { requestedDate: string; requestNote: string }) => {
    if (actionId == null) return
    createDeadlineRequest.mutate(
      { actionId, ...input },
      {
        onSuccess: () => {
          setDeadlineRequestOpen(false)
          toast.show({
            severity: 'success',
            summary: t('deadlineRequests.toast.created'),
            life: 3000,
          })
        },
      },
    )
  }

  /**
   * Alanları sunucu değeriyle senkron tutar.
   *
   * <p><b>Ölçüt `data.id` DEĞİL, sunucudaki DEĞERLER</b> (2026-08-30, push kapısı review bulgusu).
   * Öncesinde yalnız id değişiminde okunuyordu; sonuç: sorumlu "Durum değiştir" ile Gecikti-çıkış
   * yapıp sunucu YENİ TERMİNİ yazdığında yerel `deadline` eski kalıyordu. Ardından not eklemek
   * istediğinde istemci `deadlineChanged` sanıp ESKİ termini gönderiyor, sunucu da alan değişimini
   * yönetim-dışı çağrıda reddettiği için ({@code updateSubAction}) kullanıcı gerekçesiz bir
   * <b>403</b> alıyordu — tek çıkışı sayfayı yenilemekti.
   *
   * <p><b>Bilinçli ödünç:</b> sunucu değeri değişirse kullanıcının yazmakta olduğu metin üzerine
   * yazılır. Alternatif (hiç tazelememek) yukarıdaki 403'ü geri getirir; üstelik alanlar yönetim-dışı
   * kullanıcıda zaten salt-okumadır, yani çakışma yalnız yönetimde ve nadir bir yarışta doğar.
   */
  const serverRef = useRef<{
    id: number | null
    name: string
    description: string
    deadline: string
  }>({
    id: null,
    name: '',
    description: '',
    deadline: '',
  })
  useEffect(() => {
    if (!data) return
    const srv = {
      id: data.id,
      name: data.name ?? '',
      description: data.description ?? '',
      deadline: data.deadline ?? '',
    }
    const prev = serverRef.current
    if (
      prev.id !== srv.id ||
      prev.name !== srv.name ||
      prev.description !== srv.description ||
      prev.deadline !== srv.deadline
    ) {
      setName(srv.name)
      setDescription(srv.description)
      setDeadline(srv.deadline)
      serverRef.current = srv
    }
  }, [data])

  // Termin alt sınırı her durumda ≥ bugün (bağımsız aksiyonun alt aksiyonunda da) — geçmiş termin seçilemez.
  const minDeadline = deadlineMinDate(data?.meetingDate?.slice(0, 10))
  // 066 — alt aksiyon termini üst aksiyonun termininden ileri seçilemez (maxDate = üst aksiyon termini).
  const maxDeadline = parseISODate(data?.parentDeadline ?? '') ?? undefined
  /**
   * Gecikti-çıkış gerekçesi ZORUNLU mu? Özel Kalem / alt görev statü yetkisi taşıyan kullanıcıda
   * <b>hayır</b> (isteğe bağlı), yalnız görevi <b>oluşturan</b> kullanıcıda <b>evet</b> — ana görevdeki
   * "Ana Sorumlu zorunlu, yönetim isteğe bağlı" kuralının alt görev karşılığı (22.09.2026).
   *
   * <p>Ölçüt yalnız YETKİye bakar, kayda değil: "oluşturan mıyım" bilgisi istemcide yok. Yön güvenli —
   * yetkisi olmayan kullanıcıya not zorunlu görünür, sunucu da ondan zaten notu ister.
   */
  const exitNoteRequired = !hasAnyGrant(GRANTS.MINUTES_CREATE, GRANTS.SUBACTION_STATUS_UPDATE)
  /** Üst görevin termini dünde mi kaldı? Öyleyse gecikti-çıkışta seçilebilir tek bir gün bile yok. */
  const parentDeadlinePassed = !!maxDeadline && maxDeadline < todayStart()
  /**
   * 087 — Hedef teslim tarihi talebinde seçilebilir gün var mı? Talep takvimi [bugün, ana görev
   * tarihi] aralığıdır ve alt görevin mevcut tarihi devre dışıdır. Ana görev tarihi geçmişse aralık
   * boştur; ana görev tarihi bugünse ve alt görevin tarihi de bugünse tek gün devre dışı kalır.
   * Talep geciken görevde de açılabildiği için (14.10.2026) bu durum artık gerçekten oluşuyor.
   */
  const noRequestableDate =
    parentDeadlinePassed ||
    (!!maxDeadline &&
      maxDeadline.getTime() === todayStart().getTime() &&
      data?.deadline === data?.parentDeadline)
  const openDeadlineRequest = () => {
    // Pop-up boş takvimle açılmaz; çözüm yolu söylenir: önce ana görevin tarihi güncellenmeli.
    if (noRequestableDate) {
      toast.warn(t('deadlineRequests.create.noSelectableDate'))
      return
    }
    setDeadlineRequestOpen(true)
  }

  // Hedef listesi TAMAMEN SUNUCUDAN gelir (`allowedTargets`) ve istemci onu DARALTMAZ (22.09.2026).
  //
  // Önceden burada ikinci bir kural vardı: "Aksiyon Bekleniyor(330) yalnız statü yetkisine açık" →
  // `canManageStatus` olmayan kullanıcıdan elenirdi. Yeni kuralda alt görevi Tamamlandı'dan geri
  // çekmeyi ana/alt görevi OLUŞTURAN da yapabiliyor; istemci kopyası kalsaydı oluşturan, sunucunun
  // izin verdiği hedefi ekranda GÖREMEZDİ. Kuralın tek kaynağı sunucudur (ActionCommandService).
  //
  // Liste yalnız HEDEF statüleri taşır (mevcut statü listeye konmaz — 2026-08-10).
  const visibleSubTargets = statusId != null ? (transitions.data ?? []) : []
  // Tekrarsızlaştırma korunuyor: `baseStatusOf` bugün kimlik dönüşümüdür (gecikmeli çeşit kalktı), ama
  // liste sunucudan geldiği için yine de tek satırlık bir savunma olarak duruyor.
  const baseSubTargets = Array.from(
    new Set(
      visibleSubTargets.map((sid) => baseStatusOf(sid)).filter((sid): sid is number => sid != null),
    ),
  )
  // Statü ADI tek kaynaktan: DB katalogu.
  const statusName = useActionStatusName()
  // Etiketler DB katalogundan (i18n yalnız yedek) — ActionDetailPage ile aynı gerekçe.
  const statusOptions = baseSubTargets.map((sid) => ({
    value: sid,
    label: statusName(sid),
  }))

  /**
   * Ana görevle **aynı kural** (kullanıcı isteği 29.09.2026): Aksiyon Bekleniyor (330) iken açılır
   * liste yerine tek bir "Onaya Gönder" düğmesi. Alt görev matrisinde de o statüden tek hedef 331'dir
   * (`MeetingActionStatus.SUBACTION_TRANSITIONS`). Koşul statüye değil **izinli hedefe** bakar:
   * yetki/kapsam süzgeci 331'i elerse düğme de çizilmez.
   */
  const sendForApprovalOnly =
    statusId === ACTION_STATUS.PENDING &&
    baseSubTargets.length === 1 &&
    baseSubTargets[0] === ACTION_STATUS.COMPLETED

  // Gecikti (termini geçmiş) alt aksiyonu AÇIK statüye (yalnız Bekleniyor(330); "Devam Ediyor"/335 statü
  // kataloğundan kaldırıldı) çekerken yeni termin (bugün+) ZORUNLU → popup'ta alınır; alt aksiyonda NOT
  // yoktur (066/AK-7). Terminal (Tamamlandı/İptal) ve gecikmemiş aksiyon doğrudan geçer.
  const overdueSubExitTargets: number[] = [ACTION_STATUS.PENDING]
  const onStatusChange = (to: number) => {
    // `to` TEMEL id. Liste yalnız hedefleri içerdiğinden "aynı statüye geçiş" guard'ı GEREKMEZ.
    if (!data) return
    if (isOverdue && overdueSubExitTargets.includes(to)) {
      /*
       * ÜST GÖREVİN TERMİNİ GEÇMİŞSE takvim açılmaz (kullanıcı bildirimi 22.09.2026): yeni termin
       * bugün+ olmak zorunda ve üst görevinkini aşamaz, yani seçilebilir gün kalmıyor. Pop-up açılıp
       * boş takvim göstermek yerine çözüm yolu söylenir: önce ANA görevin tarihi güncellenmeli.
       * Sunucu da aynı kapıyı taşır (400 `parentDeadlinePassed`) — bu yalnız erken geri bildirimdir.
       */
      if (parentDeadlinePassed) {
        toast.error(t('actions.detail.overdueExit.parentDeadlinePassed'))
        return
      }
      setExitTarget(to)
      setExitDeadline('')
      setExitNote('')
      return
    }
    // 105 — Tamamlandı(331) hedefinde ÖNCE onaya gönderme notu sorulur (ZORUNLU, 29.09.2026); akış ana görevle AYNIDIR
    // (kullanıcı kararı 2026-09-16). Pop-up iptal edilirse statü DEĞİŞMEZ: kullanıcı kararından
    // vazgeçebilmelidir.
    if (to === ACTION_STATUS.COMPLETED) {
      setCompletionNoteOpen(true)
      return
    }
    applyStatusChange(to)
  }

  /**
   * Statü yazımı — doğrudan ya da onaya gönderme notu pop-up'ından sonra çağrılır.
   *
   * <p>`files` yalnız pop-up yolundan gelir ve statü **yazıldıktan sonra** yüklenir (30.09.2026):
   * statü yazılamazsa (bayat `modDate` → 409, yetki → 403) alt görev eskisi gibi kalmalı, ona
   * bağlanacak ek de yüklenmemelidir. Ters sıra, gerçekleşmeyen bir gönderime ait dosya bırakırdı.
   *
   * <p>Dosya hatası statü değişikliğini <b>geri almaz</b> ve onu başarısız göstermez: statü zaten
   * yazıldı. Kullanıcı ayrı bir uyarı görür ve dosyaları Dokümanlar bölümünden ekleyebilir — ana
   * görev ekranındaki kuralın aynısı.
   */
  const applyStatusChange = (to: number, completionNote?: string | null, files?: File[]) => {
    if (!data) return
    changeStatus.mutate(
      { toStatusId: to, modDate: data.modDate, completionNote },
      {
        onSuccess: () => {
          if (to === ACTION_STATUS.COMPLETED) playSuccessSound()
          toast.success(t('actions.detail.status.changed'))
          if (files && files.length > 0) {
            uploadAttachments.mutate(files, {
              onError: (e) =>
                toast.error(t(documentMutationErrorKey(e, 'actions.detail.documents.uploadError'))),
            })
          }
        },
        onError: (err) => toast.error(t(statusChangeErrorKey(err))),
      },
    )
  }

  // Gecikti-çıkış popup: yeni termin (bugün+) zorunlu; alt aksiyonda not YOK. Başarıda cache tazelenir → sayfa yenilenir.
  const submitOverdueExit = () => {
    if (exitTarget == null || !data) return
    if (exitDeadline === '') {
      toast.error(t('actions.detail.overdueExit.deadlineRequired'))
      return
    }
    // Gerekçe notu: Özel Kalem / alt görev statü yetkisinde İSTEĞE BAĞLI, yalnız-oluşturan kullanıcıda
    // ZORUNLU — sunucudaki kuralın (subNoteOutcome) aynası. Ana görevdeki ölçütün alt görev karşılığı.
    if (exitNoteRequired && exitNote.trim() === '') {
      toast.error(t('actions.detail.overdueExit.noteRequired'))
      return
    }
    changeStatus.mutate(
      {
        toStatusId: exitTarget,
        modDate: data.modDate,
        newDeadline: exitDeadline,
        note: exitNote.trim() === '' ? undefined : exitNote.trim(),
      },
      {
        onSuccess: () => {
          setExitTarget(null)
          setExitNote('')
          if (exitTarget === ACTION_STATUS.COMPLETED) playSuccessSound()
          toast.success(t('actions.detail.status.changed'))
        },
        onError: (err) => toast.error(t(statusChangeErrorKey(err))),
      },
    )
  }

  // Hedef teslim tarihi değişti mi? Not zorunluluğunun ölçütü (ana görevle aynı — 7.8). Durum
  // değişkenlerinden SONRA tanımlanır (`deadline` yukarıda useState ile kuruluyor).
  const deadlineChanged = data != null && deadline !== (data.deadline ?? '')

  const onSave = () => {
    if (!data) return
    if (name.trim() === '') {
      toast.error(t('actions.create.required'))
      return
    }
    // Açıklama denetimi YOK: alan isteğe bağlıdır (kullanıcı kararı 2026-09-14). Oradaki eski denetim
    // yalnız alanı düzenleyebilen kullanıcı için koşuyordu; zorunluluk kalkınca koşulun kendisi de gereksiz
    // kaldı. Sunucu aynı yönde değişti (bkz. ActionDetailService#updateSubAction).
    if (deadline === '') {
      toast.error(t('actions.detail.overdueExit.deadlineRequired'))
      return
    }
    // Hedef teslim tarihi değiştiyse not ZORUNLU (ana görevle aynı kural, 2026-08-28) — sunucu da
    // reddeder (400 `note`), burada erken yakalanır ki kullanıcı boş bir istek göndermesin.
    const noteText = note.trim()
    if (deadlineChanged && noteText === '') {
      toast.error(t('actions.detail.notes.deadlineRequired'))
      return
    }
    // Atanan burada gönderilmez; ayrı "Ata" akışı (dialog + buton). Alan + not TEK işlemde gider.
    update.mutate(
      {
        name: name.trim(),
        description: description.trim(),
        deadline,
        note: noteText === '' ? undefined : noteText,
      },
      {
        onSuccess: () => {
          toast.success(t('actions.detail.subActionPage.saved'))
          setNote('')
          setNoteDialogOpen(false)
        },
        // Hatanın NEDENİ gösterilir: deadlineAfterParent burada da fırlatılır (oluşturma yoluyla ortak metin).
        onError: (err) => toast.error(t(subActionSaveErrorKey(err))),
      },
    )
  }

  // "Kaydet": hedef teslim tarihi değişip not boşsa notu pop-up'ta iste (zorunlu), aksi halde doğrudan
  // kaydet — ana görevdeki `onSaveFields` ile aynı akış.
  const onSaveFields = () => {
    if (deadlineChanged && note.trim() === '') {
      setNoteDialogOpen(true)
      return
    }
    onSave()
  }

  // Not düzenle (ACTION_NOTE_MANAGE) — seçili notu taslağa al, pop-up aç.
  const openEditNote = (n: ActionNote) => {
    setEditNote(n)
    setEditNoteText(n.note)
  }
  const submitEditNote = () => {
    if (!editNote) return
    const text = editNoteText.trim()
    if (text === '') {
      toast.error(t('actions.detail.notes.required'))
      return
    }
    updateNoteMut.mutate(
      { noteId: editNote.id, note: text, modDate: editNote.modDate },
      {
        onSuccess: () => {
          toast.success(t('actions.detail.notes.updated'))
          setEditNote(null)
        },
        onError: (e) => toast.error(t(noteMutationErrorKey(e, 'actions.detail.notes.updateError'))),
      },
    )
  }
  // Sil (soft-delete) — yıkıcı işlem, onay iste (Anayasa).
  const confirmNoteDelete = (n: ActionNote) =>
    confirmDialog({
      group: 'subaction-note-delete',
      header: t('actions.detail.notes.delete'),
      message: t('actions.detail.notes.deleteConfirm'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('common.delete'),
      accept: () =>
        deleteNoteMut.mutate(n.id, {
          onSuccess: () => toast.success(t('actions.detail.notes.deleted')),
          onError: (e) =>
            toast.error(t(noteMutationErrorKey(e, 'actions.detail.notes.deleteError'))),
        }),
    })

  // 073 — "Ata": dialog'da seçilen sorumluyu butonla API'ye gönder; başarıda cache tazelenir → sayfa yenilenir.
  const onSubmitAssign = () => {
    if (!selected) return
    reassign.mutate(minutePersonToInput(selected), {
      onSuccess: () => {
        setAssignOpen(false)
        setSelected(null)
        toast.success(t('actions.detail.subActionPage.assign.done'))
      },
      onError: () => toast.error(t('actions.detail.subActionPage.assign.error')),
    })
  }

  // "Geri": origin (geldiği konum — useNavigateWithOrigin ile taşınan path+query; ör. /minutes/actions/<scope>
  // alt sayfası dahil) varsa oraya döner; yoksa (derin link/yenileme) alt aksiyonun kök aksiyon detayına.
  //
  // YEDEK YOL `parentVisible` İLE KAPILANIR: ana görev detayının ucu, kullanıcı onu göremiyorsa
  // 404 döner ve "Geri" kullanıcıyı doğrudan hata ekranına götürürdü (bağımsız code-review
  // bulgusu). Derin link ya da F5 sonrası router state'i kaybolduğu için bu yedek gerçekten
  // kullanılır — teorik değil.
  //
  // ÖLÇÜT `canSeeMainTask` DEĞİL (kullanıcı bildirimi 2026-09-10, bir tur böyle yazıldı ve
  // "Geri"nin ana göreve dönme davranışını bozdu). İkisi bugün yalnız KUTU koşuluyla ayrılır
  // (`canSeeMainTask` = `parentVisible` && kutu "Hayır" değil) ve "Geri" kutuya BAKMAMALIDIR:
  // kutu işaretsizken de kullanıcı ana görevi kendi ilişkisiyle görebiliyor olabilir, geri
  // dönebilmelidir. Doğru soru "bu kişi ana görevi açabilir mi"dir; onu sunucu `parentVisible`
  // ile yanıtlar.
  const fromState = (location.state ?? null) as { from?: string } | null
  const backTo =
    fromState?.from ??
    (data?.parentVisible && data.parentId != null
      ? `/minutes/actions/${data.parentId}`
      : '/minutes/actions')
  /**
   * Origin varsa bu sayfaya GERÇEKTEN oradan push edilerek gelinmiştir → "Geri" `navigate(-1)` ile
   * yığından DÜŞER; `navigate(backTo)` yeni bir giriş PUSH ederdi (bağımsız code-review bulgusu).
   *
   * Ana görev sayfası bu kalıbı bir kullanıcı bildirimiyle zaten uygulamıştı; alt görev sayfasında
   * eksikti ve 2026-09-10'da ana görev → alt görev geçişine origin eklenince **detay ↔ detay**
   * döngüsü ilk kez mümkün oldu: liste → görev → alt görev → Geri → görev → tarayıcı geri tuşu →
   * yine alt görev (kullanıcı listeyi bekler) ve yığın gidip geldikçe büyür.
   */
  const hasOrigin = Boolean(fromState?.from)

  // Bu sayfa yalnız alt aksiyon içindir; kök aksiyon id'si → normal Aksiyon Detay'a yönlendir.
  if (data && data.parentId == null) {
    return <Navigate to={`/minutes/actions/${aid}`} replace state={location.state} />
  }
  if (isError) {
    return <div className="page minutes-page">{t('actions.detail.status.error')}</div>
  }

  return (
    <div className="page minutes-page">
      <div className="mtg-detail">
        <div className="act-detail-topbar">
          <Button
            variant="secondary"
            size="small"
            icon="pi pi-arrow-left"
            label={t('actions.detail.subActionPage.back')}
            onClick={() => (hasOrigin ? navigate(-1) : navigate(backTo))}
          />
          {/* Görev Geçmişi (kullanıcı isteği 24.09.2026): ana görevdeki düğmenin alt görev karşılığı,
              aynı yerde (geri düğmesinin hemen sağında) ve aynı bileşenle. Uç zaten alt görevi
              destekliyor — `listForAction` kaydın KENDİ izlerini ve (varsa) altlarının izlerini
              döndürür; alt görevin altı olmadığı için sonuç tam olarak bu alt görevin geçmişidir.
              Yetki de detayla aynıdır (`requireActionViewable`, parentId'ye göre). */}
          <Button
            variant="secondary"
            size="small"
            icon="pi pi-history"
            label={t('actions.detail.subActionPage.history')}
            onClick={() => setHistoryOpen(true)}
          />
          {/*
            "Ana Görevi Gör" — alt görev oluşturulurken "atanan ana görevi de görebilsin"
            işaretlenmişse VE kullanıcı ana görevi görebiliyorsa çizilir; otomatik üretilen alt
            görevlerde kutu her zaman işaretlidir. Kutu işaretliyse kısayol yalnız alt görevin
            sorumlusuna değil, yetkisi olan HERKESE açıktır (kullanıcı kuralı 2026-09-10 — aynı gün
            önce "sadece sorumlu" yazılmış, sonra geri alınmıştı). Ana görev SALT-OKUMA açılır ve bu, popup'ın YAPISINDAN gelir: bileşen
            yalnız okunan bölümleri çizer (MainTaskViewDialog), yani ana görev `editable=true`
            gelse bile hiçbir kontrol görünmez. Sunucu tarafında da kapalıdır — yazma kapsamı
            alt→üst sıçraması taşımaz (MeetingMinutesRepository.ACTION_WRITE_NAV_PREFIX); iki
            koruma bilinçle üst üstedir, ekranınki tek başına yeterli sayılmaz.

            Koşul SUNUCUDAN okunur (data.canSeeMainTask), istemcide yeniden hesaplanmaz. İddia TEK
            YÖNLÜ: bayrak true ise ana görev ucu KESİNLİKLE açıktır — sunucudaki ölçüt doğrudan
            okuma kapsamıdır (parentVisible), yani buton 404'e götürmez. Tersi geçerli değildir:
            false, ucun kapalı olduğunu göstermez — kutu "Hayır" işaretliyken kullanıcı ana görevi
            kendi ilişkisiyle yine de açabilir, yalnız kısayol çizilmez.

            Sayfaya GİTMEZ, popup açar (kullanıcı isteği 2026-09-10): alt görev bağlamı korunur,
            kullanıcı ana görevi görmek için alt görevinden çıkmak zorunda kalmaz.
          */}
          {data?.canSeeMainTask && data.parentId != null && (
            <Button
              variant="secondary"
              size="small"
              icon="pi pi-sitemap"
              label={t('actions.detail.subActionPage.viewMainTask')}
              onClick={() => setMainTaskOpen(true)}
            />
          )}
          {/* 073 — Sorumlu "Ata" (SAP/HR): statü Dropdown'ının solunda. */}
          {canAssign && (
            <Button
              variant="secondary"
              size="small"
              icon="pi pi-user-edit"
              label={t('actions.detail.subActionPage.assign.button')}
              onClick={() => {
                setSelected(null)
                setAssignOpen(true)
              }}
            />
          )}
          {canChangeStatus && sendForApprovalOnly && (
            <Button
              size="small"
              icon="pi pi-send"
              label={t('actions.detail.status.sendForApproval')}
              onClick={() => onStatusChange(ACTION_STATUS.COMPLETED)}
              disabled={changeStatus.isPending}
            />
          )}
          {canChangeStatus && !sendForApprovalOnly && statusOptions.length > 0 && (
            <Dropdown
              value={null}
              placeholder={t('actions.detail.status.change')}
              options={statusOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => onStatusChange(e.value as number)}
              className="act-status-select"
              disabled={changeStatus.isPending}
            />
          )}
        </div>

        {/*
          Termin Bilgi Kartı — Aksiyon Detay ile aynı: renkli termin/kalan gün, üstte (7.5).
          087: hedef teslim tarihi güncelleme talebi butonu ALT AKSİYONDA DA olmalıdır (FR-002/2 alt
          aksiyonu 330 ve 332'de açıkça uygun sayar). Ana aksiyon detayı alt aksiyonu bu sayfaya
          yönlendirdiği için, buton yalnız orada bağlanırsa alt aksiyona hiç ulaşmıyordu.
        */}
        <ActionTerminCard
          deadline={data?.deadline ?? null}
          statusId={statusId}
          /* Kısıtlı Yetkili asistan kipinde talep AÇILAMAZ (126 FR-019): ölçüt tamamen
             sunucudan ve ilişki tabanlı geliyor (yöneticinin kendi görevinde `true`), uç ise
             `HAS_ACTION_READ_ANY` ile korunuyor — yani okuma kodlarıyla açılan bir KAYIT
             OLUŞTURMA ucu. Yetki listesi burada hiçbir şey yapamaz. */
          requestAllowed={(data?.deadlineRequestAllowed ?? false) && !readOnly}
          pendingRequestId={data?.pendingDeadlineRequestId ?? null}
          onRequestDeadlineUpdate={openDeadlineRequest}
        />

        {/* Form da kipe bağlı — `AgendaFormDialog` ile aynı gerekçe: düğmeyi gizlemek yetmez,
            `deadlineRequestOpen` başka bir yoldan dolarsa pop-up yine açılırdı. Bugün erişilemez
            (yalnız kapatılan düğme açıyor), ama gerekçe simetrik uygulanıyor. */}
        {!readOnly && (
          <DeadlineRequestDialog
            visible={deadlineRequestOpen}
            currentDeadline={data?.deadline ?? null}
            parentDeadline={data?.parentDeadline ?? null}
            submitting={createDeadlineRequest.isPending}
            onSubmit={submitDeadlineRequest}
            onHide={() => setDeadlineRequestOpen(false)}
          />
        )}

        <section className="minutes-section act-subinfo-section">
          <div className="minutes-section-head">
            <h2>{t('actions.detail.subActionPage.title')}</h2>
            {/*
              "Gecikti" — katalogda böyle bir statü YOKTUR (085/FR-012: gecikme statüden değil
              `IS_DELAYED_UPDATED` bayrağından türetilir); yalnızca kırmızı bir işarettir. Bayrağı sunucu
              değerlendirir (`overdueLabel`); 2026-08-17'den beri kayıt Tamamlandı'ya geçse de görünür.
              Ana Aksiyon Detay ile aynı konum (kullanıcı isteği 2026-08-17): statü hücresinin yanı değil
              bölüm başlığının sağı — iki ekran aynı yerde okunur.
            */}
            {data?.overdueLabel && (
              <span className="act-overdue-label">{t('actions.status.overdueLabel')}</span>
            )}
          </div>

          <dl className="mtg-info-grid">
            <div>
              <dt>{t('actions.columns.number')}</dt>
              <dd>{data?.recordNumber ?? ''}</dd>
            </div>
            {/* Atanan — 073: SAP/HR'den değiştirilebilir; "Ata" butonu topbar'da (statü Dropdown'ının solunda). */}
            <div>
              <dt>{t('actions.form.assignee')}</dt>
              <dd>{data?.assignees?.[0]?.fullName ?? ''}</dd>
            </div>
            <div>
              <dt>{t('actions.columns.status')}</dt>
              <dd>
                {/* "Gecikti" etiketi buradan BÖLÜM BAŞLIĞINA taşındı (kullanıcı isteği 2026-08-17) —
                    aynı ekranda iki kez çizilmesin diye burada tekrarlanmıyor. */}
                <ActionStatusTag statusId={statusId} name={data?.statusName ?? ''} />
              </dd>
            </div>
            {/* Görevi Oluşturan (2026-09-10, kullanıcı isteği) — ana görev detayındaki hücrenin
                aynısı. Sunucu adı çözer (CRE_BY → USERS); kayıt yoksa "#id" yedeği döner. */}
            <div>
              <dt>{t('actions.detail.info.createdBy')}</dt>
              <dd>{data?.createdByName ?? '—'}</dd>
            </div>
            {/* Kapanma Tarihi — yalnız kapanmış kayıtta (closureDate dolu). */}
            {data?.closureDate && (
              <div>
                <dt>{t('actions.detail.info.closureDate')}</dt>
                <dd>{formatDate(data.closureDate)}</dd>
              </div>
            )}
          </dl>

          {/* Yerleşim ANA GÖREVLE AYNI (kullanıcı isteği 2026-09-09): Başlık + Açıklama iki kolonda
              yan yana (`act-desc-grid`, ≤720px'te tek kolona düşer), Hedef Teslim Tarihi altta ve dar
              (`act-field--inline`). Öncesinde üçü de tam genişlikteydi ve tarih alanı ekranı boydan
              boya kaplıyordu. */}
          <div className="act-desc-grid">
            <label className="act-field">
              <span className="ui-field-label">
                {t('actions.detail.subActions.name')}{' '}
                {fieldsEditable && <span className="ui-req">*</span>}
              </span>
              <InputTextarea
                value={name}
                rows={3}
                autoResize
                maxLength={250}
                disabled={!fieldsEditable}
                onChange={(e) => setName(e.target.value)}
              />
            </label>

            <label className="act-field">
              <span className="ui-field-label">{t('actions.detail.subActions.description')}</span>
              {/* 2026-09-09 — açıklama oluşturma popup'ıyla birlikte eklendi; girilen metnin bir daha
                  görülemediği bir alan bırakmamak için detay da düzenlenebilir yapıldı (kullanıcı
                  kararı). Sınır 2000: ana görevle ve DB kolonuyla (VARCHAR2(2000)) hizalı. Alan
                  düzenleme yetkisi adla AYNI (`fieldsEditable`) — sunucu da öyle davranır; tarih 130'dan
                  beri ayrı (`deadlineFieldEditable`). */}
              <InputTextarea
                value={description}
                rows={3}
                autoResize
                maxLength={2000}
                disabled={!fieldsEditable}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
          </div>

          <label className="act-field act-field--inline act-desc-deadline">
            <span className="ui-field-label">
              {t('actions.form.deadline')} {deadlineFieldEditable && <span className="ui-req">*</span>}
            </span>
            {/* Gecikti'de inline termin DISABLE (madde 4): yeni termin, statü değişimi popup'ında alınır. */}
            {/*
                Termin GECİKMİŞ alt aksiyonda da değiştirilebilir (kullanıcı kuralı 2026-08-17, ana
                aksiyonla aynı gerekçe). Alan 2026-11-02'den beri ad/açıklamadan AYRI ölçütle açılır
                (`deadlineFieldEditable` — 130: `ACTION_DEADLINE_UPDATE` ∨ oluşturan); öncesinde
                `fieldsEditable`, yani `MINUTES_CREATE` idi. Sınırlar korunur: `minDeadline` >= bugün,
                `maxDeadline` = üst aksiyonun termini (alt ≤ ana).

                ÖNCESİ `disabled={!fieldsEditable || isOverdue}` idi ve bu, gecikmiş alt aksiyonu ÇIKIŞSIZ
                bırakıyordu (bağımsız code-review bulgusu): inline alan kapalı, 087 talebi "termini
                geçmemiş" ön koşuluyla reddediliyor, Gecikti-çıkış pop-up'ı ise yalnız hedef 330 iken
                açılıyor ama alt aksiyon geçiş matrisinde 330→330 yok → pop-up hiç açılmıyordu. Yani üst
                aksiyonun termini ileri çekilse bile alt kayıt geçmiş terminiyle donuyordu.
              */}
            <Calendar
              value={parseISODate(deadline)}
              minDate={minDeadline}
              maxDate={maxDeadline}
              dateFormat="dd.mm.yy"
              showIcon
              disabled={!deadlineFieldEditable}
              onChange={(e) => setDeadline(toISODate(e.value ?? null))}
            />
          </label>

          {/*
            Görev Notu (2026-08-28) — ana görevle AYNI bileşen: kronolojik liste, "Not Ekle" pop-up'ı ve
            en altta Kaydet. Kaydet bu bölümün içindedir çünkü alan + not TEK istekte gider (7.10);
            ayrı bir buton bırakılsaydı iki kaydetme yolu oluşurdu.
            066/AK-7'nin "alt görevde not yoktur" kuralı bu kullanıcı kararıyla kalktı; doküman kolu
            da 2026-09-10'da kalktı (aşağıdaki Dokümanlar bölümü) ve izin ölçütleri ORTAKtır.
          */}
          <ActionNotesSection
            notes={data?.notes ?? []}
            // `fieldsEditable` DEĞİL: alt görevin sorumlusu ad/termini düzenleyemez ama not ekleyebilir
            // (kullanıcı kuralı 2026-08-30). Bölümün "Kaydet"i alan+notu tek istekte gönderir; sorumlu
            // için ad/termin zaten değişmemiş gider, sunucu da yalnız onu kabul eder.
            // Tarih açıkken de: tarih değişikliği not ister ve aynı "Kaydet"ten gider — yetkili ama
            // sorumlu/yönetim olmayan kullanıcı (130) aksi hâlde tarihi seçip kaydedemezdi.
            editable={notesAddable || deadlineFieldEditable}
            canManageNotes={notesManageable}
            note={note}
            noteDialogOpen={noteDialogOpen}
            deadlineChanged={deadlineChanged}
            pending={update.isPending}
            onNote={setNote}
            onNoteDialog={setNoteDialogOpen}
            onSubmitNote={onSave}
            onSaveFields={onSaveFields}
            onEditNote={openEditNote}
            onDeleteNote={confirmNoteDelete}
            saveLabel="actions.detail.subActionPage.save"
          />
          {notesManageable && (
            <>
              <NoteEditDialog
                open={editNote != null}
                text={editNoteText}
                pending={updateNoteMut.isPending}
                onText={setEditNoteText}
                onSubmit={submitEditNote}
                onClose={() => setEditNote(null)}
              />
              <ConfirmDialog group="subaction-note-delete" />
            </>
          )}
        </section>

        {/*
          Tamamlanma Notu — <b>Dokümanlar'ın ÜSTÜNDE, kendi kutusu</b> (kullanıcı isteği 22.09.2026).
          Önceden görev kartının İÇİNDE, bilgi ızgarasının altındaydı: kart zaten kutuyken içine ikinci
          bir çerçeve giriyordu ve not, düzenlenebilir alanların (Başlık/Açıklama/Tarih) arasında
          kalıyordu. Artık sayfanın kendi bölümlerinden biri — Görev Notu ile Dokümanlar arasında.
        */}
        <ActionCompletionNote note={data?.completionNote} />

        {/*
          Dokümanlar (2026-09-10, kullanıcı isteği: "alt görev ekranına dosya ekleme özelliğini
          ekleyelim ana görevdeki gibi") — ana görevle AYNI bileşen ve aynı kural. 066/AK-7'nin
          "alt görevde doküman yoktur" kuralı bu kararla kalktı; not kolu da 2026-08-28'de aynı
          gerekçeyle kalkmıştı. Sunucudaki `notAllowedForSubAction` kapısı da bu turda kaldırıldı —
          ekran açılıp uç 400 dönseydi kullanıcı dosyayı seçip hata alırdı.
        */}
        <ActionDocumentsSection
          actionId={aid}
          documents={data?.documents ?? []}
          editable={documentsEditable}
        />

        {/* 073 — "Ata": SAP/HR'den seç → footer butonuyla gönder; başarıda cache tazelenir → sayfa yenilenir. */}
        <Dialog
          visible={assignOpen}
          header={t('actions.detail.subActionPage.assign.title')}
          modal
          dismissableMask
          draggable={false}
          style={{ width: '32rem', maxWidth: '95vw' }}
          onHide={() => {
            setAssignOpen(false)
            setSelected(null)
          }}
          footer={
            <div className="ui-dialog-footer">
              <Button
                icon="pi pi-check"
                label={t('actions.detail.subActionPage.assign.submit')}
                onClick={onSubmitAssign}
                loading={reassign.isPending}
                disabled={!selected}
              />
            </div>
          }
        >
          <p className="act-empty-hint">{t('actions.detail.subActionPage.assign.hint')}</p>
          <SinglePersonEditor
            person={selected}
            editable
            canClear
            disableWhenSet
            onSet={(p) => setSelected(personInputToMinutePerson(p))}
            onClear={() => setSelected(null)}
            placeholder={t('actions.detail.assignees.placeholder')}
            removeLabel={t('actions.detail.assignees.remove')}
          />
        </Dialog>

        {/* Gecikti alt görevi Aksiyon Bekleniyor'a çekerken yeni termin (bugün+) ZORUNLU, gerekçe notu
            ana görevdeki gibi sorulur (22.09.2026 kullanıcı isteği; 066/AK-7'nin "alt görevde not yok"
            kuralı kalktı). Kapanış hedeflerinde (Tamamlandı/Kapatıldı) pop-up açılmaz. */}
        <Dialog
          visible={exitTarget != null}
          header={t('actions.detail.overdueExit.title')}
          modal
          dismissableMask
          draggable={false}
          style={{ width: '32rem', maxWidth: '95vw' }}
          onHide={() => setExitTarget(null)}
          footer={
            <div className="ui-dialog-footer">
              <Button
                icon="pi pi-check"
                label={t('actions.detail.overdueExit.save')}
                onClick={submitOverdueExit}
                loading={changeStatus.isPending}
              />
            </div>
          }
        >
          <p className="act-empty-hint">
            {exitNoteRequired
              ? t('actions.detail.overdueExit.hintOwner')
              : t('actions.detail.overdueExit.hintManager')}
          </p>
          <div className="act-form">
            <label className="act-field act-field--full">
              <span className="ui-field-label">
                {t('actions.detail.overdueExit.deadlineLabel')} <span className="ui-req">*</span>
              </span>
              <Calendar
                value={parseISODate(exitDeadline)}
                minDate={overdueExitMinDate}
                maxDate={maxDeadline}
                dateFormat="dd.mm.yy"
                showIcon
                onChange={(e) => setExitDeadline(toISODate(e.value ?? null))}
              />
            </label>
            {/* Gerekçe (not) — ana görev pop-up'ıyla AYNI kalıp: alan her rolde görünür, yıldız yalnız
                zorunlu olduğunda. Doldurulursa görevin kalıcı notu olarak yazılır. */}
            <label className="act-field act-field--full">
              <span className="ui-field-label">
                {t('actions.detail.overdueExit.noteLabel')}
                {exitNoteRequired && <span className="ui-req"> *</span>}
              </span>
              <InputTextarea
                value={exitNote}
                rows={3}
                autoResize
                maxLength={4000}
                onChange={(e) => setExitNote(e.target.value)}
              />
            </label>
          </div>
        </Dialog>

        {/*
          105 — Onaya gönderme notu. Alt görev 331'e çekilirken araya girer; not ZORUNLUDUR (29.09.2026)
          ama pop-up iptal edilirse görev tamamlanmaz. Bileşen ana görev ekranıyla ORTAKTIR: iki
          yüzeyin sınırı, sayacı ve uyarı metni ayrışamaz.
        */}
        <CompletionNoteDialog
          visible={completionNoteOpen}
          submitting={changeStatus.isPending}
          /* Dokümanlar bölümüne verilen ÖLÇÜTÜN AYNISI — gerekçe ana görev ekranında ve prop'un
             kendi javadoc'unda: dosya yükleme ucu ayrı bir yetki ister. */
          filesEnabled={documentsEditable}
          onSubmit={(completionNote, files) => {
            setCompletionNoteOpen(false)
            applyStatusChange(ACTION_STATUS.COMPLETED, completionNote, files)
          }}
          onHide={() => setCompletionNoteOpen(false)}
        />

        {/*
          "Ana Görevi Gör" — ana görev detayı SALT-OKUMA popup olarak (kullanıcı isteği 2026-09-10).
          `parentId` non-null daraltması yukarıdaki butonla aynı; popup kapalıyken veri çekilmez.
        */}
        {data?.parentId != null && (
          <MainTaskViewDialog
            actionId={data.parentId}
            visible={mainTaskOpen}
            onHide={() => setMainTaskOpen(false)}
          />
        )}
      </div>
      {/* Baslik da dugmeyle AYNI seyi soyler: ana gorev ekraninda "Islem Gecmisi" yaziyor, burada
          kapsamin alt gorev oldugunu belirtmek kullanicinin istegi. */}
      <ActionHistoryDialog
        actionId={aid}
        title={t('actions.detail.subActionPage.history')}
        visible={historyOpen}
        onHide={() => setHistoryOpen(false)}
      />

    </div>
  )
}
